import { useState } from 'react'
import { InputBase, Stack, Typography } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useToast } from '@/shared/contexts/ToastContext'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'

import type {
  NutritionCustomer,
  NutritionCustomerInput,
  NutritionPatientId,
} from './api'
import { normalizeBirthDate } from '@/shared/utils/birthDate'
import { SexSelect } from './SexSelect'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { useUpdateNutritionCustomer } from './useNutritionCustomers'

const FIELDS = [
  { key: 'name', label: '氏名', placeholder: '山田 花子' },
  { key: 'name_kana', label: 'ふりがな', placeholder: 'やまだ はなこ' },
  { key: 'sex', label: '性別', placeholder: '女' },
  { key: 'birth_date', label: '生年月日', placeholder: '19650304' },
] as const

type FieldKey = (typeof FIELDS)[number]['key']
type Draft = Record<FieldKey, string>

const draftOf = (customer: NutritionCustomer): Draft => ({
  name: customer.name,
  name_kana: customer.name_kana,
  sex: customer.sex,
  birth_date: customer.birth_date ?? '',
})

// 打ちかけの空行を 1 つ足した行 (= 足す場所)。
export const patientIdRows = (
  patientIds: NutritionPatientId[]
): NutritionPatientId[] => [...patientIds, { store: '', id: '' }]

// 送る形。店舗も ID も空の行は落とす (back も同じ判定をする)。
export const filledPatientIds = (
  rows: NutritionPatientId[]
): NutritionPatientId[] =>
  rows
    .map((row) => ({ store: row.store.trim(), id: row.id.trim() }))
    .filter((row) => row.store || row.id)

const labelSx = {
  flexShrink: 0,
  width: 88,
  fontSize: scaledFontSize('label'),
  color: colorTokens.fg.muted,
} as const

interface Props {
  customer: NutritionCustomer
}

// お客様の情報 (カルテの ⋯ から開く dialog) の中身: 4 欄と患者 ID の `店舗 : ID`。
// 欄を離れた時点で、変わった欄だけを送る。直るのはお客様 (マスタ) だけで、記録に写した
// 報告書の欄は変わらない (報告書はその時点の文書)。design-book 04-apps/kakeru.md。
export const NutritionCustomerPanel = ({ customer }: Props) => {
  const { showToast } = useToast()
  const save = useUpdateNutritionCustomer(customer.id)

  const saved = draftOf(customer)
  const [draft, setDraft] = useState<Draft>(saved)
  const [rows, setRows] = useState(patientIdRows(customer.patient_ids))
  // サーバの値が変わったら (保存の応答・別の端末からの保存) 手元を追従させる
  const [seen, setSeen] = useState(customer)
  if (seen !== customer) {
    setSeen(customer)
    setDraft(draftOf(customer))
    setRows(patientIdRows(customer.patient_ids))
  }

  const commit = (input: NutritionCustomerInput) => save.mutate(input)

  const commitField = (key: FieldKey) => {
    const value = draft[key].trim()
    if (value === (saved[key] ?? '')) return
    if (key === 'birth_date') {
      const birthDate = value ? normalizeBirthDate(value) : null
      if (value && birthDate === null) {
        showToast('生年月日は 19650304 の形で入れてください', 'error')
        return
      }
      commit({ birth_date: birthDate })
      return
    }
    // 氏名は空にできない (お客様を引く唯一の手がかり)。空で離れたら元に戻す
    if (key === 'name' && !value) {
      setDraft({ ...draft, name: saved.name })
      return
    }
    commit({ [key]: value })
  }

  const commitPatientIds = (next: NutritionPatientId[]) => {
    const filled = filledPatientIds(next)
    if (JSON.stringify(filled) === JSON.stringify(customer.patient_ids)) return
    commit({ patient_ids: filled })
  }

  const setRow = (index: number, patch: Partial<NutritionPatientId>) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    )
    const last = next[next.length - 1]
    setRows(last.store || last.id ? patientIdRows(next) : next)
  }

  return (
    <Stack spacing={0.75} sx={{ maxWidth: 560 }}>
      {FIELDS.map((field) => (
        <Stack key={field.key} direction="row" alignItems="center" spacing={1}>
          <Typography component="span" sx={labelSx}>
            {field.label}
          </Typography>
          {field.key === 'sex' ? (
            // 選んだ時点で保存する (離れるのを待つ値が無い)
            <SexSelect
              value={draft.sex}
              onChange={(sex) => {
                setDraft({ ...draft, sex })
                if (sex !== saved.sex) commit({ sex })
              }}
            />
          ) : (
            <InputBase
              value={draft[field.key]}
              onChange={(e) =>
                setDraft({ ...draft, [field.key]: e.target.value })
              }
              onBlur={() => commitField(field.key)}
              placeholder={field.placeholder}
              inputProps={{ 'aria-label': field.label }}
              sx={fieldInputSx}
            />
          )}
        </Stack>
      ))}
      <Typography
        component="span"
        sx={{
          ...labelSx,
          width: 'auto',
          pt: 1.5,
          fontWeight: FONT_WEIGHT.medium,
        }}
      >
        患者 ID
      </Typography>
      {/* 出力用で、店舗ごとに別の ID が混ざる (1 人に N 個・必須でない)。空の行が足す場所 */}
      {rows.map((row, index) => (
        <Stack key={index} direction="row" alignItems="center" spacing={1}>
          <InputBase
            value={row.store}
            onChange={(e) => setRow(index, { store: e.target.value })}
            onBlur={() => commitPatientIds(rows)}
            placeholder="店舗"
            inputProps={{ 'aria-label': `患者 ID の店舗 ${index + 1}` }}
            sx={{ ...fieldInputSx, flex: 1.4 }}
          />
          <InputBase
            value={row.id}
            onChange={(e) => setRow(index, { id: e.target.value })}
            onBlur={() => commitPatientIds(rows)}
            placeholder="ID"
            inputProps={{ 'aria-label': `患者 ID ${index + 1}` }}
            sx={fieldInputSx}
          />
        </Stack>
      ))}
    </Stack>
  )
}
